/**
 * Centralized Action Ordering Configuration
 *
 * Defines ordering for all action buttons across tables.
 *
 * Actions are alphabetically sorted with two exceptions:
 * - Share always appears second-to-last
 * - Export always appears last
 *
 * @module components/actions/config
 */

export type ActionName =
  | 'Annotate'
  | 'Change Severity'
  | 'Change Status'
  | 'Custom'
  | 'Export'
  | 'Import'
  | 'Manage'
  | 'Note'
  | 'Scan'
  | 'Share';

/**
 * Standard action order (alphabetical with Share, Import, and Export always at end)
 *
 * Note: Share is always third-to-last, Import is always second-to-last, Export is always last
 */
export const LEFT_ACTION_ORDER: ActionName[] = [
  'Annotate',
  'Note', // Standalone Add Note button
  'Change Severity',
  'Change Status',
  'Manage',
  'Scan',
  'Share', // Always third-to-last
  'Import', // Always second-to-last
  'Export', // Always last
];

/**
 * Always-enabled actions that appear on the right side (currently none)
 */
export const RIGHT_ACTION_ORDER: ActionName[] = [];

/**
 * Get display order for a section's actions
 *
 * @param availableActions - Actions available in the section
 * @returns Ordered actions split into left and right groups
 *
 * @example
 * ```typescript
 * const { left, right } = getActionOrder([
 *   'Annotate',
 *   'Scan',
 *   'Share',
 *   'Manage',
 *   'Export',
 * ]);
 * // left = ['Annotate', 'Manage', 'Scan', 'Share']
 * // right = ['Export']
 * ```
 */
export function getActionOrder(availableActions: ActionName[]): {
  left: ActionName[];
  right: ActionName[];
} {
  const left = LEFT_ACTION_ORDER.filter(
    action =>
      availableActions.includes(action) && !RIGHT_ACTION_ORDER.includes(action)
  );

  const right = RIGHT_ACTION_ORDER.filter(action =>
    availableActions.includes(action)
  );

  return { left, right };
}
